/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { isPublicWeb } from './platform';

export const STALE_SESSION_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours
export const STALE_SESSION_TIMEOUT_TEXT = '2 hours';

export const CLIENT_TARGET_KEY = 'poker_client_target_id';
export const JOIN_TARGET_KEY = 'poker_join_target';
export const ACTIVE_APP_STORAGE_KEY = 'deckduel_current_app';
export const JOIN_CODE_QUERY_PARAM = 'pokerJoin';
export const JOIN_CODE_REGEX = /^\d{6}$/;

/**
 * Extracts a 6-digit poker join code from a URL search param or hash param.
 */
export const extractJoinCodeFromUrl = (urlString?: string | null): string | null => {
  if (!urlString) return null;

  try {
    let safeUrl = urlString;
    if (safeUrl.startsWith('airchips://')) {
      safeUrl = safeUrl.replace('airchips://', 'http://airchips.local/');
    }

    const url = new URL(safeUrl);
    const directCode = url.searchParams.get(JOIN_CODE_QUERY_PARAM);
    if (directCode && JOIN_CODE_REGEX.test(directCode)) {
      return directCode;
    }

    if (url.hash.startsWith('#')) {
      const hashParams = new URLSearchParams(url.hash.slice(1));
      const hashCode = hashParams.get(JOIN_CODE_QUERY_PARAM);
      if (hashCode && JOIN_CODE_REGEX.test(hashCode)) {
        return hashCode;
      }
    }
  } catch (e) {
    console.error('Failed to parse deep link URL:', e);
  }

  return null;
};

/**
 * Safely persists an incoming 6-digit join target across session and local storage.
 */
export const setPendingJoinCode = (code: string): void => {
  if (!JOIN_CODE_REGEX.test(code)) return;
  try {
    sessionStorage.setItem(JOIN_TARGET_KEY, code);
  } catch {}
  try {
    localStorage.setItem(JOIN_TARGET_KEY, code);
  } catch {}
};

/**
 * Retrieves a pending join target from session or local storage if valid.
 */
export const getPendingJoinCode = (): string | null => {
  if (typeof window === 'undefined') return null;
  try {
    const sessionCode = sessionStorage.getItem(JOIN_TARGET_KEY);
    if (sessionCode && JOIN_CODE_REGEX.test(sessionCode)) return sessionCode;
  } catch {}
  try {
    const localCode = localStorage.getItem(JOIN_TARGET_KEY);
    if (localCode && JOIN_CODE_REGEX.test(localCode)) return localCode;
  } catch {}
  return null;
};

/**
 * Clears pending join target from both storage mechanisms.
 */
export const clearPendingJoinCode = (): void => {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(JOIN_TARGET_KEY);
  } catch {}
  try {
    localStorage.removeItem(JOIN_TARGET_KEY);
  } catch {}
};

/**
 * Atomically retrieves and clears the pending join target.
 */
export const consumePendingJoinCode = (): string | null => {
  const code = getPendingJoinCode();
  if (code) {
    clearPendingJoinCode();
  }
  return code;
};


/**
 * Validates whether a raw serialized poker session represents an active host session
 * that is not stale and not a completed tournament with closed results.
 */
export const isValidHostSession = (sessionRaw: string | null, sessionUid?: string): boolean => {
  if (!sessionRaw) return false;
  try {
    const parsed = JSON.parse(sessionRaw);
    if (parsed?.isClientState) return false;

    const isCompletedTournament = parsed?.tournamentState?.isEnabled && parsed?.tournamentState?.isCompleted;
    const hasTournamentResultsToShow = isCompletedTournament && parsed?.tournamentState?.showResultsModal;
    if (isCompletedTournament && !hasTournamentResultsToShow) {
      return false;
    }

    if (sessionUid && typeof window !== 'undefined' && window.localStorage) {
      const lastActive = localStorage.getItem(`poker_last_active_${sessionUid}`);
      if (lastActive) {
        const lastActiveTime = parseInt(lastActive, 10);
        if (!Number.isNaN(lastActiveTime) && Date.now() - lastActiveTime > STALE_SESSION_TIMEOUT_MS) {
          return false;
        }
      }
    }

    return true;
  } catch {
    return false;
  }
};

/**
 * Determines whether there is currently an active, resumable poker session
 * in localStorage (host, connected client, or pending join code).
 */
export const hasActivePokerSession = (uid?: string): boolean => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return false;
  }

  if (isPublicWeb()) {
    return false;
  }

  try {
    // 1. Check for active client target
    const clientTarget = localStorage.getItem(CLIENT_TARGET_KEY);
    if (clientTarget) {
      return true;
    }

    // 2. Check for pending direct join code
    const joinTarget = localStorage.getItem(JOIN_TARGET_KEY);
    if (joinTarget) {
      return true;
    }

    // 3. Check for active host session for known user UID
    if (uid) {
      const hostSession = localStorage.getItem(`poker_active_session_${uid}`);
      if (isValidHostSession(hostSession, uid)) {
        return true;
      }
    } else {
      // If user UID is not yet loaded (e.g. before auth finishes), scan localStorage
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('poker_active_session_')) {
          const sessionUid = key.slice('poker_active_session_'.length);
          const rawSession = localStorage.getItem(key);
          if (isValidHostSession(rawSession, sessionUid)) {
            return true;
          }
        }
      }
    }
  } catch (err) {
    console.warn('Failed to check active poker session:', err);
  }

  return false;
};
